import { useCallback, useEffect, useState } from 'react';
import { useGoogleAuth } from '@questlabs/auth-js';
import { ncbData } from '../api';

const OWNERS_TABLE = 'memorial_owners_1779000000';

/**
 * Ownership is decided by the server. A row in memorial_owners is stamped with
 * the caller's verified Greta identity, and per-user reads only return the
 * caller's own rows. So a row for this memorial coming back means the signed-in
 * person is the owner. The table has a UNIQUE memorial_id, so only one person
 * can ever claim a memorial.
 */
export function useMemorialOwner(memorial) {
  const { user: googleUser } = useGoogleAuth();
  const memorialId = memorial?.id ? String(memorial.id) : '';
  const signedIn = !!googleUser;

  const [isOwner, setIsOwner] = useState(false);
  const [checking, setChecking] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setClaimError('');
    if (!signedIn || !memorialId) {
      setIsOwner(false);
      setChecking(false);
      return undefined;
    }
    setChecking(true);
    ncbData('read', OWNERS_TABLE, { memorial_id: memorialId }).then((res) => {
      if (cancelled) return;
      const rows = Array.isArray(res?.data) ? res.data : [];
      setIsOwner(
        res?.status === 'success' &&
          rows.some((r) => String(r?.memorial_id) === memorialId)
      );
      setChecking(false);
    });
    return () => { cancelled = true; };
  }, [signedIn, memorialId, googleUser?.sub]);

  const claim = useCallback(async () => {
    if (!signedIn || !memorialId) return;
    setClaiming(true);
    setClaimError('');
    const res = await ncbData('create', OWNERS_TABLE, {
      memorial_id: memorialId,
      memorial_name: memorial?.name ?? '',
    });
    if (res?.status === 'success') {
      setIsOwner(true);
    } else {
      setClaimError('This memorial already has an owner, or the claim could not be saved.');
    }
    setClaiming(false);
  }, [signedIn, memorialId, memorial?.name]);

  return { isOwner, checking, signedIn, claim, claiming, claimError };
}

export default useMemorialOwner;